import { useMemo, useState } from 'react';
import { articles, topicCategories } from '@/mocks/home';

interface ArticleSectionProps {
  query: string;
  activeCategory: string;
  onCategoryChange: (category: string) => void;
  onQueryChange: (query: string) => void;
}

const ALL = '全部';

export default function ArticleSection({
  query,
  activeCategory,
  onCategoryChange,
  onQueryChange,
}: ArticleSectionProps) {
  const [draft, setDraft] = useState(query);
  const chips = [ALL, ...topicCategories.map((c) => c.name)];

  const filtered = useMemo(() => {
    const kw = query.trim().toLowerCase();
    return articles.filter((article) => {
      const matchCategory = activeCategory === ALL || article.category === activeCategory;
      const matchKeyword =
        !kw ||
        article.title.toLowerCase().includes(kw) ||
        article.excerpt.toLowerCase().includes(kw) ||
        article.category.toLowerCase().includes(kw);
      return matchCategory && matchKeyword;
    });
  }, [query, activeCategory]);

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onQueryChange(draft.trim());
  };

  const handleChip = (chip: string) => {
    onCategoryChange(chip);
  };

  const handleReset = () => {
    setDraft('');
    onQueryChange('');
    onCategoryChange(ALL);
  };

  return (
    <section id="articles" className="w-full bg-background-50 py-16 md:py-24 scroll-mt-20">
      <div className="w-full px-4 md:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
          <div>
            <p className="text-xs tracking-[0.3em] text-primary-600 font-medium mb-2">NEW GOSSIP</p>
            <h2 className="font-heading font-black text-2xl md:text-4xl text-foreground-950">
              最新瓜料
            </h2>
            <p className="mt-3 text-sm text-foreground-600">
              共 {filtered.length} 篇 · 已核对公开信息，未确认部分会明确标注
            </p>
          </div>

          <form onSubmit={handleSearch} role="search" className="flex gap-2 w-full lg:w-auto">
            <div className="relative flex-1 lg:w-72">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400">
                <i className="ri-search-line" aria-hidden="true"></i>
              </span>
              <input
                type="search"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="输入关键词筛选"
                aria-label="筛选瓜料关键词"
                className="w-full h-11 pl-9 pr-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
              />
            </div>
            <button
              type="submit"
              className="h-11 px-5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
            >
              筛选
            </button>
          </form>
        </div>

        <div className="flex flex-wrap gap-2 mb-8">
          {chips.map((chip) => {
            const active = activeCategory === chip;
            return (
              <button
                key={chip}
                type="button"
                onClick={() => handleChip(chip)}
                aria-pressed={active}
                className={`px-4 py-2 rounded-full text-sm transition-colors whitespace-nowrap cursor-pointer ${
                  active
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-600'
                }`}
              >
                {chip}
              </button>
            );
          })}
        </div>

        {filtered.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {filtered.map((article) => (
              <article
                key={article.id}
                className="group flex flex-col bg-background-50 border border-background-200/70 hover:border-primary-300 rounded-lg overflow-hidden transition-colors"
              >
                <a href="#articles" className="block w-full h-48 md:h-52 overflow-hidden">
                  <img
                    src={article.image}
                    alt={`${article.title} 配图`}
                    title={`${article.title} - 黑吃瓜网最新瓜料`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </a>
                <div className="flex flex-col flex-1 p-5">
                  <div className="flex items-center gap-3 text-xs text-foreground-500 mb-3">
                    <span className="px-2 py-0.5 rounded bg-accent-100 text-accent-800 whitespace-nowrap">
                      {article.category}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line" aria-hidden="true"></i>
                      {article.readTime}
                    </span>
                  </div>
                  <h3 className="font-heading font-bold text-base md:text-lg text-foreground-950 leading-snug group-hover:text-primary-600 transition-colors">
                    <a href="#articles">{article.title}</a>
                  </h3>
                  <p className="mt-3 text-sm text-foreground-600 leading-relaxed flex-1">
                    {article.excerpt}
                  </p>
                  <div className="mt-5 pt-4 border-t border-background-100 flex items-center justify-between text-xs text-foreground-500">
                    <span className="flex items-center gap-1.5">
                      <i className="ri-user-line" aria-hidden="true"></i>
                      {article.author}
                    </span>
                    <span>{article.date}</span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="py-20 text-center border border-dashed border-background-300 rounded-lg bg-background-50">
            <span className="mx-auto w-14 h-14 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 text-2xl">
              <i className="ri-search-eye-line" aria-hidden="true"></i>
            </span>
            <p className="mt-4 font-heading font-bold text-foreground-950">暂时没有匹配的瓜料</p>
            <p className="mt-2 text-sm text-foreground-600">
              换个关键词，或者看看其他分类里正在聊的话题。
            </p>
            <button
              type="button"
              onClick={handleReset}
              className="mt-6 px-5 h-10 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
            >
              重置筛选条件
            </button>
          </div>
        )}
      </div>
    </section>
  );
}